import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/types/drama';

interface HeroCarouselProps {
  items: Drama[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      setIndex((next + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = items[index];

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={item.banner}
            alt={`${item.title}主图`}
            title={`${item.title} 在线播放`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/30" />

      <div className="relative z-10 h-full w-full flex items-center px-4 md:px-16 lg:px-24">
        <div className="w-full max-w-2xl pt-16">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-medium text-foreground-950 whitespace-nowrap">
              <i className="ri-fire-fill" />
              本季热播
            </span>
            <span className="inline-flex items-center rounded-full border border-background-50/30 px-3 py-1 text-[11px] text-background-50/90 whitespace-nowrap">
              {current.category}
            </span>
          </div>
          <h1 className="font-heading font-extrabold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight">
            {current.title}
          </h1>
          <p className="mt-3 text-accent-300 font-medium text-sm md:text-base">{current.subtitle}</p>
          <p className="mt-4 text-background-50/85 text-sm md:text-base leading-relaxed max-w-xl line-clamp-3">
            {current.synopsis}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur-sm hover:bg-background-50/20 transition-colors"
            >
              <i className="ri-information-line text-lg" />
              剧情详情
            </Link>
          </div>

          <div className="flex items-center gap-2 mt-8">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到${item.title}`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-4 md:right-16 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一部"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 text-xl backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一部"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 text-xl backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    </section>
  );
}